import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {PullRequestErrorState} from '../components/PullRequestErrorState'
import {pullRequestsChangesRoute, pullRequestsChangesWithRangeRoute} from './files-changed-routes'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {pullRequestsLayoutRoute} from './layout-route'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {useEffect, useEffectEvent, useMemo, useRef, useState} from 'react'
import type {PullRequest} from '../types'
import type {FilesRoutePayload} from './route-payload-types'
import {AnalyticsProvider} from '@github-ui/analytics-provider'
import {usePullRequestAnalytics} from '../hooks/use-pull-request-analytics'
import {useMemoryStats} from '@github-ui/use-memory-stats'
import {sendCustomMetric} from '@github-ui/stats'
import {getCurrentDiffImplementation} from '../helpers/get-diff-implementation'
import {DiffComparisonViewer} from '../components/DiffComparisonViewer'
import {useFileMetadata} from '../page-data/loaders/use-file-metadata'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {
  mergeFileMetadataIntoChangesData,
  reportFileMetadataError,
  type FileMetadataErrorReason,
} from '../page-data/loaders/merge-file-metadata'
import type {FileMetadataResponse} from '@github-ui/dotcom-schema/types/pull_requests/v1/file_metadata'

interface FilesAnalyticsProps {
  viewSettings: FilesRoutePayload['user']['viewSettings']
}

export function ChangesEntrypoint() {
  const [loadStartedAt] = useState(() => performance.now())

  return (
    <ErrorBoundary
      critical
      boundaryName="ChangesEntrypoint"
      fallback={<PullRequestErrorState text="Changes cannot be loaded" />}
    >
      <Changes loadStartedAt={loadStartedAt} />
    </ErrorBoundary>
  )
}

export function ChangesWithRangeEntrypoint() {
  return (
    <ErrorBoundary
      critical
      boundaryName="ChangesWithRangeEntrypoint"
      fallback={<PullRequestErrorState text="Changes cannot be loaded" />}
    >
      <ChangesWithRange />
    </ErrorBoundary>
  )
}

function Changes({loadStartedAt}: {loadStartedAt: number}) {
  const {
    data: {pullRequest},
  } = useRouteQuery(pullRequestsLayoutRoute, 'mainQuery')
  const {data: routeData} = useRouteQuery(pullRequestsChangesRoute, 'mainQuery')
  const cacheSplitEnabled = isFeatureEnabled('prs_files_changed_cache_split_routes')

  return cacheSplitEnabled ? (
    <ChangesWithMetadata pullRequest={pullRequest} routeData={routeData} loadStartedAt={loadStartedAt} />
  ) : (
    <>
      <ChangesLoadTiming cacheSplitEnabled={false} loadStartedAt={loadStartedAt} />
      <FilesChangedComponent
        pullRequest={pullRequest}
        routeData={routeData}
        // force remount when routeData updates after soft navigation so the component gets fresh data
        key={`${routeData.pullRequest.comparison.baseOid}-${routeData.pullRequest.comparison.headOid}`}
      />
    </>
  )
}

function ChangesWithMetadata({
  pullRequest,
  routeData,
  loadStartedAt,
}: {
  pullRequest: PullRequest
  routeData: FilesRoutePayload
  loadStartedAt: number
}) {
  const {baseOid, headOid} = routeData.pullRequest.comparison
  const metadataQuery = useFileMetadata({
    owner: routeData.repository.ownerLogin,
    repo: routeData.repository.name,
    pullRequestNumber: String(pullRequest.number),
    baseOid,
    headOid,
  })

  return (
    <>
      <ChangesLoadTiming cacheSplitEnabled loadStartedAt={loadStartedAt} />
      <ErrorBoundary
        key={`${baseOid}-${headOid}`}
        boundaryName="ChangesFileMetadata"
        fallback={<FilesChangedComponent pullRequest={pullRequest} routeData={routeData} />}
      >
        <FilesChangedWithMetadata pullRequest={pullRequest} routeData={routeData} metadata={metadataQuery.data} />
      </ErrorBoundary>
    </>
  )
}

function FilesChangedWithMetadata({
  pullRequest,
  routeData,
  metadata,
}: {
  pullRequest: PullRequest
  routeData: FilesRoutePayload
  metadata: FileMetadataResponse | undefined
}) {
  const {routeData: completeRouteData, errorReasons} = useMemo(
    () =>
      metadata
        ? mergeFileMetadataIntoChangesData(routeData, metadata)
        : {routeData, errorReasons: [] as FileMetadataErrorReason[]},
    [metadata, routeData],
  )
  const reportedReasonsRef = useRef(new Set<FileMetadataErrorReason>())
  const {baseOid, headOid} = routeData.pullRequest.comparison

  useEffect(() => {
    for (const reason of new Set(errorReasons)) {
      if (reportedReasonsRef.current.has(reason)) continue

      reportedReasonsRef.current.add(reason)
      reportFileMetadataError(reason)
    }
  }, [errorReasons])

  return <FilesChangedComponent key={`${baseOid}-${headOid}`} pullRequest={pullRequest} routeData={completeRouteData} />
}

function ChangesLoadTiming({cacheSplitEnabled, loadStartedAt}: {cacheSplitEnabled: boolean; loadStartedAt: number}) {
  const hasReportedRef = useRef(false)

  useEffect(() => {
    if (hasReportedRef.current) return
    hasReportedRef.current = true

    sendCustomMetric({
      name: 'PULL_REQUESTS_FILES_CHANGED_LOAD_DURATION',
      value: performance.now() - loadStartedAt,
      tags: {cacheSplit: cacheSplitEnabled ? 'enabled' : 'disabled'},
    })
  }, [cacheSplitEnabled, loadStartedAt])

  return null
}

function ChangesWithRange() {
  const {
    data: {pullRequest},
  } = useRouteQuery(pullRequestsLayoutRoute, 'mainQuery')
  const {data: routeData} = useRouteQuery(pullRequestsChangesWithRangeRoute, 'mainQuery')
  const {range} = useRouteParams(pullRequestsChangesWithRangeRoute)

  return <FilesChangedComponent pullRequest={pullRequest} routeData={routeData} key={range} />
}

export function FilesAnalytics({viewSettings}: FilesAnalyticsProps) {
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  const sendPageView = useEffectEvent(() => {
    sendPullRequestAnalyticsEvent('page_view.files_page', 'PAGE_VIEW', {
      browser_width: typeof window !== 'undefined' ? window.innerWidth : 0,
      diff_view_setting: viewSettings.splitPreference,
      comments_setting: viewSettings.commentsPreference,
      line_spacing_setting: viewSettings.lineSpacing,
      change_groups_setting: viewSettings.showChangeGroups ? 'show' : 'hide',
    })
  })

  useEffect(() => {
    sendPageView()
  }, [])

  return null
}

export function FilesChangedComponent({
  pullRequest,
  routeData,
}: {
  pullRequest: PullRequest
  routeData: FilesRoutePayload
}) {
  const diffImplementation = getCurrentDiffImplementation(routeData.virtualizeDiffEntries)

  useMemoryStats({
    statName: 'PULL_REQUESTS_FILES_JS_HEAP',
    tags: {
      version: diffImplementation.version,
      virtualized: String(diffImplementation.virtualized),
    },
  })

  return (
    <AnalyticsProvider
      appName="pull-request-files"
      category="files_tab"
      metadata={{
        pull_request_id: pullRequest.id.toString(),
        base_sha: routeData.pullRequest.comparison.baseOid,
        head_sha: routeData.pullRequest.comparison.headOid,
        diff_version: diffImplementation.version,
        diff_virtualized: String(diffImplementation.virtualized),
      }}
    >
      <FilesAnalytics viewSettings={routeData.user.viewSettings} />
      <DiffComparisonViewer.Viewer
        basePath={pullRequest.pathName}
        initialData={routeData}
        useAdvancedToolbar
        useChangeGroups
        useInlineComments
        useEditOptions
        useFileTree
      />
    </AnalyticsProvider>
  )
}
